import React, { useState, useEffect } from "react";
import {
  GoogleMap,
  Circle,
  Marker,
  InfoWindow,
  useLoadScript,
  Polygon,
} from "@react-google-maps/api";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Stack,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Backdrop,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Avatar,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Whatshot as FireIcon,
  Cloud as SmokeIcon,
  Layers as LayersIcon,
  Undo as UndoIcon,
  ContentCopy as ContentCopyIcon,
  LocationOn as LocationIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Videocam as VideocamIcon,
  EventNote as EventNoteIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import { selectSiteData } from "../reusable_components/site_data/siteDataSlice";
import { backend_url, googleMapsApiKey } from "../App";
import JSZip from "jszip";
import convertXmlToGeoJSON from "../features/events/convertXmlToGeoJSON";
import * as turf from "@turf/turf";
import SensorList from "./SensorList";
import EventList from "./EventList";
import SideNav from "../Headers/SideNav/SideNav";
import "./map.css";

// Import logos
import forest_fire_new_logo from "../images/forest_fire_new_logo.jpeg";
import fireLogo from "../images/firelogo.jpeg";

function MapContainer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey,
  });

  // Get current user from localStorage
  const [isPunjabAdmin, setIsPunjabAdmin] = useState(false);

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      setIsPunjabAdmin(userData?.username === "punjabadmin");
    } catch {
      setIsPunjabAdmin(false);
    }
  }, []);

  const [map, setMap] = useState(null);
  const defaultCenter = { lat: 34.19167584003779, lng: 73.23095807365446 };
  const [center, setCenter] = useState({
    center: defaultCenter,
    zoom: 9,
    isZoom: false,
  });
  const [selected, setSelected] = useState({ theta: 0, item: {} });
  const [circleCenter, setCircleCenter] = useState({
    lat: 34.534508,
    lng: 73.003801,
  });
  const [circleRadius, setCircleRadius] = useState(10000);
  const [fireFootprints, setFireFootprints] = useState(null);
  const { side_nav: side_nav_check } = useSelector(selectSiteData);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState(null);

  // State for cones based on fire/smoke detection (only latest event)
  const [fireCones, setFireCones] = useState([]);
  const [showFireCones, setShowFireCones] = useState(false);
  const [loadingCones, setLoadingCones] = useState(false);

  // State for hovered cone info
  const [hoveredCone, setHoveredCone] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(true);

  // Filter states
  const [filterType, setFilterType] = useState("all");

  // Dashboard modal state
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [selectedSensorForDashboard, setSelectedSensorForDashboard] =
    useState(null);

  const side_nav = side_nav_check ? <SideNav /> : null;
  const mapStyles = {
    height: "calc(100vh - 80px)",
    width: "100%",
  };

  const handleMapLoad = (map) => {
    setMap(map);
  };

  // Function to calculate angle based on frame index
  const calculateAngleForFrame = (
    frameIndex,
    totalFrames,
    startAngle,
    endAngle,
  ) => {
    if (totalFrames <= 1) return startAngle;
    const angleStep = (endAngle - startAngle) / (totalFrames - 1);
    return startAngle + angleStep * frameIndex;
  };

  // Function to calculate a single cone at a specific angle
  const calculateConeAtAngle = (
    position,
    angleDegrees,
    fovDegrees = 25,
    rangeMeters = 800,
  ) => {
    if (!position || !position.lat || !position.lng)
      return { conePath: [], centerPoint: null };

    const { lat, lng } = position;
    const directionRad = ((angleDegrees - 90) * Math.PI) / 180;
    const halfFovRad = ((fovDegrees / 2) * Math.PI) / 180;
    const leftAngleRad = directionRad - halfFovRad;
    const rightAngleRad = directionRad + halfFovRad;

    const rangeLat = rangeMeters / 111320;
    const rangeLng = rangeMeters / (111320 * Math.cos((lat * Math.PI) / 180));

    const leftPoint = {
      lat: lat + Math.sin(leftAngleRad) * rangeLat,
      lng: lng + Math.cos(leftAngleRad) * rangeLng,
    };

    const rightPoint = {
      lat: lat + Math.sin(rightAngleRad) * rangeLat,
      lng: lng + Math.cos(rightAngleRad) * rangeLng,
    };

    const centerPoint = {
      lat: lat + Math.sin(directionRad) * rangeLat,
      lng: lng + Math.cos(directionRad) * rangeLng,
    };

    return {
      conePath: [{ lat, lng }, leftPoint, rightPoint, { lat, lng }],
      centerPoint,
    };
  };

  // Get cone color based on type
  const getConeColor = (specie, isHovered = false) => {
    if (specie === "fire") {
      return isHovered ? "#FF5555" : "#FF0000";
    }
    return isHovered ? "#FFaa55" : "#FF6600";
  };

  // Filter cones based on selected filter
  const getFilteredCones = () => {
    if (!fireCones.length) return [];
    if (filterType === "fire") {
      return fireCones.filter((cone) => cone.specie === "fire");
    } else if (filterType === "smoke") {
      return fireCones.filter((cone) => cone.specie === "smoke");
    } else if (filterType === "none") {
      return [];
    }
    return fireCones;
  };

  // Helper function to get camera status
  const getCameraStatus = (camera) => {
    const isLive = camera?.live === true;
    const lastReportedAt = camera?.last_reported_at;

    if (!isLive) {
      return {
        label: "UNARMED",
        color: "error",
        iconColor: "#d32f2f",
        chipColor: "error",
        pulse: false,
      };
    }

    if (!lastReportedAt) {
      return {
        label: "OFFLINE",
        color: "warning",
        iconColor: "#ed6c02",
        chipColor: "warning",
        pulse: false,
      };
    }

    const lastReported = new Date(lastReportedAt);
    const now = new Date();
    const diffInHours = (now - lastReported) / (1000 * 60 * 60);

    if (diffInHours <= 1) {
      return {
        label: "ARMED",
        color: "success",
        iconColor: "#2e7d32",
        chipColor: "success",
        pulse: true,
      };
    } else {
      return {
        label: "OFFLINE",
        color: "warning",
        iconColor: "#ed6c02",
        chipColor: "warning",
        pulse: false,
      };
    }
  };

  // Fetch ONLY the latest fire/smoke event for a camera and create cones
  const fetchLatestFireEventForCamera = async (camera) => {
    setLoadingCones(true);
    setShowInfoWindow(true);
    setFilterType("all");
    setFireCones([]);
    setShowFireCones(false);

    try {
      const cameraDetailResponse = await fetch(
        `${backend_url}/core/api/camera/${camera.id}/`,
        {
          headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        },
      );
      const fullCameraData = await cameraDetailResponse.json();

      const startAngle = fullCameraData.start_angle || 0;
      const endAngle = fullCameraData.end_angle || 180;
      const totalFrames = fullCameraData.video_interval || 15;
      const coneRangeMeters = 800;
      const coneFOV = 25;

      const eventsResponse = await fetch(
        `${backend_url}/core/api/event/?cameras=${camera.id}&page_size=50`,
        {
          headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        },
      );
      const eventsData = await eventsResponse.json();

      const events = eventsData.results || [];
      if (events.length === 0) {
        setFireCones([]);
        setShowFireCones(false);
        setLoadingCones(false);
        return;
      }

      const sortedEvents = [...events].sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      );
      const latestEvent = sortedEvents[0];

      const imagesResponse = await fetch(
        `${backend_url}/core/api/image/?event=${latestEvent.uuid}&page_size=100`,
        {
          headers: {
            Authorization: `Token ${localStorage.getItem("token")}`,
          },
        },
      );
      const imagesData = await imagesResponse.json();

      const eventImages = (imagesData.results || [])
        .filter((img) => img.camera === camera.id)
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      if (eventImages.length === 0) {
        setFireCones([]);
        setShowFireCones(false);
        setLoadingCones(false);
        return;
      }

      const uniqueDetections = new Map();

      for (let frameIndex = 0; frameIndex < eventImages.length; frameIndex++) {
        const img = eventImages[frameIndex];

        const boxesResponse = await fetch(
          `${backend_url}/core/api/box/?image=${img.id}`,
          {
            headers: {
              Authorization: `Token ${localStorage.getItem("token")}`,
            },
          },
        );
        const boxesData = await boxesResponse.json();

        for (const box of boxesData.results || []) {
          const specieName =
            box.specie?.name?.toLowerCase() ||
            box.specie?.id?.toLowerCase() ||
            "";
          if (specieName === "fire" || specieName === "smoke") {
            const uniqueKey = `${img.id}_${specieName}`;
            if (!uniqueDetections.has(uniqueKey)) {
              const angle = calculateAngleForFrame(
                frameIndex,
                totalFrames,
                startAngle,
                endAngle,
              );
              uniqueDetections.set(uniqueKey, {
                id: box.id,
                imageId: img.id,
                frameIndex: frameIndex,
                angle: angle,
                specie: specieName,
                imageDate: img.date,
              });
            }
          }
        }
      }

      const cones = [];
      for (const detection of Array.from(uniqueDetections.values())) {
        const { conePath, centerPoint } = calculateConeAtAngle(
          { lat: fullCameraData.latitude, lng: fullCameraData.longitude },
          detection.angle,
          coneFOV,
          coneRangeMeters,
        );
        cones.push({
          id: `${camera.id}_${detection.id}_${detection.frameIndex}`,
          angle: detection.angle,
          frameIndex: detection.frameIndex,
          path: conePath,
          centerPoint: centerPoint,
          specie: detection.specie,
          imageDate: detection.imageDate,
          cameraName: camera.description,
          cameraId: camera.id,
          eventUuid: latestEvent.uuid,
          eventDate: latestEvent.date,
        });
      }

      setFireCones(cones);
      setShowFireCones(cones.length > 0);
    } catch (error) {
      console.error("Error fetching latest fire event:", error);
    } finally {
      setLoadingCones(false);
    }
  };

  useEffect(() => {
    const parseKMZ = async () => {
      try {
        const url = `https://firms.modaps.eosdis.nasa.gov/api/kml_fire_footprints/south_asia/24h/c6.1/FirespotArea_south_asia_c6.1_24h.kmz`;
        const response = await fetch(url);
        const kmzData = await response.blob();

        const zip = await JSZip.loadAsync(kmzData);
        const kmlFile = Object.values(zip.files).find((file) =>
          file.name.endsWith(".kml"),
        );

        if (kmlFile) {
          const kmlData = await kmlFile.async("text");
          const convertedGeoJSON = await convertXmlToGeoJSON(kmlData);

          if (convertedGeoJSON && convertedGeoJSON.features) {
            let filteredFeatures;
            if (selectedInterval !== null) {
              const [min, max] = selectedInterval.split("&&");
              filteredFeatures = convertedGeoJSON.features.filter((feature) => {
                const featureTime = extractTimestamp(
                  feature.properties.description,
                );
                if (featureTime) {
                  const currentTime = new Date();
                  const timeDifference =
                    (currentTime - featureTime) / (60 * 60 * 1000);
                  return (
                    timeDifference > parseInt(min) &&
                    timeDifference <= parseInt(max)
                  );
                }
                return false;
              });
            } else {
              filteredFeatures = convertedGeoJSON.features.filter(
                (feature) => feature,
              );
            }
            setFireFootprints({
              ...convertedGeoJSON,
              features: filteredFeatures,
            });
          } else {
            setFireFootprints(null);
          }
        } else {
          setFireFootprints(null);
        }
      } catch (error) {
        console.error("Error parsing KMZ:", error);
        setFireFootprints(null);
      }
    };
    parseKMZ();
  }, [selectedInterval]);

  const onSelect = async (item) => {
    setHoveredCone(null);
    setShowInfoWindow(true);
    setFilterType("all");
    setFireCones([]);
    setShowFireCones(false);

    if (item.user || item.isCamera) {
      item.location = { lat: item.latitude, lng: item.longitude };
      await fetchLatestFireEventForCamera(item);
    } else if (item.lat && item.lng) {
      item.location = { lat: item.lat, lng: item.lng };
    } else if (item.latitude && item.longitude) {
      item.location = { lat: item.latitude, lng: item.longitude };
    }

    setSelected({ theta: selected.theta, item: item });
    setCircleCenter(item.location);
    handleMarkerClick();
  };

  const handleMarkerClick = () => {
    const newRadius = 3000;
    setCircleRadius(newRadius);
    if (map) {
      const circleBounds = new window.google.maps.Circle({
        center: circleCenter,
        radius: newRadius,
      }).getBounds();
      const markerBounds = new window.google.maps.LatLngBounds();
      markerBounds.extend(circleCenter);
      map.fitBounds(markerBounds.union(circleBounds), { padding: 100 });
      const maxZoom = 18;
      if (map.getZoom() > maxZoom) map.setZoom(maxZoom);
    }
  };

  const handleConeMouseOver = (cone) => setHoveredCone(cone);
  const handleConeMouseOut = () => setHoveredCone(null);
  const handleConeClick = (cone) => {
    setShowInfoWindow(false);
    setHoveredCone(cone);
  };

  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${backend_url}/core/api/towers/`, {
      headers: { Authorization: `Token ${localStorage.getItem("token")}` },
    })
      .then((response) => response.json())
      .then((actualData) => setData(actualData.results))
      .catch((err) => console.error(err));
  }, []);

  const getMarkerIcon = (description) => {
    const featureTime = extractTimestamp(description);
    if (featureTime) {
      const timeDifference = (new Date() - featureTime) / (60 * 60 * 1000);
      if (timeDifference <= 1) return "/maroon.png";
      if (timeDifference <= 3) return "/red.png";
      if (timeDifference <= 6) return "/orange.png";
      if (timeDifference <= 12) return "/yellow.png";
      return "/lime.png";
    }
    return "/marker-icon-2x.png";
  };

  const extractTimestamp = (description) => {
    const hiddenElement = document.createElement("div");
    hiddenElement.style.display = "none";
    hiddenElement.innerHTML = description;
    const detectionTimeElement = Array.from(
      hiddenElement.querySelectorAll("b"),
    ).find((element) => element.textContent.trim() === "Detection Time:");
    if (detectionTimeElement) {
      const timestampString = detectionTimeElement.nextSibling.nodeValue.trim();
      return new Date(timestampString);
    }
    return null;
  };

  const pakistanBoundsGeoJSON = {
    type: "Polygon",
    coordinates: [
      [
        [60.866374, 23.6345],
        [60.866374, 37.084107],
        [77.8375, 37.084107],
        [77.8375, 23.6345],
        [60.866374, 23.6345],
      ],
    ],
  };

  const isCoordinateInsidePakistan = (lat, lng) => {
    const point = turf.point([lng, lat]);
    const polygon = turf.polygon(pakistanBoundsGeoJSON.coordinates);
    return turf.booleanPointInPolygon(point, polygon);
  };

  const handleRadioChange = (val) => setSelectedInterval(val);
  const handleReset = () => {
    setSelectedInterval(null);
  };

  const filteredCones = getFilteredCones();

  const isCamera = selected.item?.user || selected.item?.isCamera;
  const cameraStatus = isCamera ? getCameraStatus(selected.item) : null;
  const isArmed = cameraStatus?.label === "ARMED";

  const filterOptions = [
    { label: "≤1 hour", value: "0&&1", color: "#800000" },
    { label: "1-3 hours", value: "1&&3", color: "#dc3545" },
    { label: "3-6 hours", value: "3&&6", color: "#fd7e14" },
    { label: "6-12 hours", value: "6&&12", color: "#ffc107" },
    { label: "12-24 hours", value: "12&&24", color: "#98fb98" },
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleOpenDashboard = (sensor) => {
    setSelectedSensorForDashboard(sensor);
    setDashboardOpen(true);
  };

  const handleCloseDashboard = () => {
    setDashboardOpen(false);
    setSelectedSensorForDashboard(null);
  };

  // Check if selected item is a sensor
  const isSensor = selected.item?.isSensor || selected.item?.sensor_type;

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "#0a0a0a",
      }}
    >
      {side_nav}

      {/* Left Sidebar */}
      <Box
        sx={{
          width: 320,
          flexShrink: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "rgba(20,20,30,0.95)",
          backdropFilter: "blur(10px)",
          borderRight: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, mt: 8.5, overflow: "auto" }}>
            <SensorList sensorSelect={onSelect} />
          </Box>
        </Box>

        <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)" }} />

        <Box sx={{ flex: 0.55, overflow: "auto" }}>
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#fff" }}>
                🗺️ Firms Hotspots
              </Typography>
              <Tooltip title="Reset Filters">
                <IconButton
                  size="small"
                  onClick={handleReset}
                  sx={{ color: "#fff" }}
                >
                  <UndoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ px: 2, py: 1 }}>
            <RadioGroup
              value={selectedInterval || ""}
              onChange={(e) => handleRadioChange(e.target.value)}
            >
              <Stack spacing={0.1}>
                {filterOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={
                      <Radio
                        sx={{
                          color: "rgba(255,255,255,0.5)",
                          "&.Mui-checked": { color: option.color },
                        }}
                      />
                    }
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            bgcolor: option.color,
                            borderRadius: "50%",
                          }}
                        />
                        <Typography variant="body2" sx={{ color: "#fff" }}>
                          {option.label}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </Stack>
            </RadioGroup>
          </Box>
        </Box>
      </Box>

      {/* Center - Map */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Top Toolbar */}
        <Paper
          elevation={0}
          sx={{
            m: 1,
            mb: 0,
            p: 1,
            borderRadius: 2,
            bgcolor: "rgba(30,30,40,0.95)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="h6"
              sx={{
                flexGrow: 1,
                fontWeight: 700,
                background: "linear-gradient(135deg, #ff6b6b, #ffd93d)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              ForestWatch Pro 🌲
            </Typography>
            <Tooltip title="Map Style">
              <IconButton size="small" sx={{ color: "#fff" }}>
                <LayersIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset View">
              <IconButton
                size="small"
                onClick={() => {
                  setCenter({ center: defaultCenter, zoom: 9, isZoom: false });
                  if (map) {
                    map.setCenter(defaultCenter);
                    map.setZoom(9);
                  }
                }}
                sx={{ color: "#fff" }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {/* Map Container */}
        <Box
          sx={{
            flexGrow: 1,
            m: 0,
            mt: 0,
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
          }}
        >
          {!isLoaded ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <GoogleMap
              mapContainerStyle={mapStyles}
              onLoad={handleMapLoad}
              zoom={center.zoom}
              center={center.center}
              options={{
                mapTypeId: "satellite",
                streetViewControl: !isMobile,
                scaleControl: !isMobile,
                mapTypeControl: !isMobile,
                rotateControl: !isMobile,
                zoomControl: !isMobile,
                fullscreenControl: !isMobile,
                clickableIcons: false,
              }}
              onClick={() => {
                // Close info window when clicking on map
                if (selected.item?.location) {
                  setSelected({ theta: 0, item: {} });
                  setShowFireCones(false);
                  setFireCones([]);
                  setShowInfoWindow(true);
                }
              }}
            >
              {/* Render Markers for Towers */}
              {data?.map((item) => (
                <Marker
                  key={item.name}
                  icon="https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                  position={{ lat: item.lat, lng: item.lng }}
                  onClick={() => onSelect(item)}
                />
              ))}

              {/* Loading indicator */}
              <Backdrop
                open={loadingCones}
                sx={{ position: "absolute", zIndex: 1000 }}
              >
                <Paper
                  sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
                >
                  <CircularProgress size={24} />
                  <Typography>Loading latest detections...</Typography>
                </Paper>
              </Backdrop>

              {/* Render Fire/Smoke Cones */}
              {showFireCones &&
                filteredCones.map((cone) => {
                  const isHovered = hoveredCone?.id === cone.id;
                  const coneColor = getConeColor(cone.specie, isHovered);
                  return (
                    <Polygon
                      key={`fire-cone-${cone.id}`}
                      path={cone.path}
                      options={{
                        fillColor: coneColor,
                        fillOpacity: isHovered ? 0.85 : 0.65,
                        strokeColor: coneColor,
                        strokeOpacity: 1,
                        strokeWeight: isHovered ? 3 : 2,
                        geodesic: true,
                        clickable: true,
                      }}
                      onMouseOver={() => handleConeMouseOver(cone)}
                      onMouseOut={handleConeMouseOut}
                      onClick={() => handleConeClick(cone)}
                    />
                  );
                })}

              {/* Hover InfoWindow for Cone */}
              {hoveredCone && hoveredCone.centerPoint && (
                <InfoWindow
                  position={hoveredCone.centerPoint}
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -5),
                    disableAutoPan: true,
                  }}
                >
                  <Paper
                    sx={{
                      p: 0,
                      minWidth: 220,
                      bgcolor:
                        hoveredCone.specie === "fire" ? "#ff4444" : "#ff9933",
                      color: "white",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0}
                      alignItems="center"
                      sx={{ mb: 0 }}
                    >
                      {hoveredCone.specie === "fire" ? (
                        <FireIcon />
                      ) : (
                        <SmokeIcon />
                      )}
                      <Typography variant="subtitle2" fontWeight="bold">
                        {hoveredCone.specie === "fire"
                          ? "🔥 FIRE DETECTED"
                          : "💨 SMOKE DETECTED"}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" display="block">
                      📍 {hoveredCone.cameraName}
                    </Typography>
                    <Typography variant="caption" display="block">
                      📐 Direction: {Math.round(hoveredCone.angle)}°
                    </Typography>
                    <Typography variant="caption" display="block">
                      🎬 Frame: {hoveredCone.frameIndex + 1}/15
                    </Typography>
                    <Typography variant="caption" display="block">
                      📅 {new Date(hoveredCone.imageDate).toLocaleString()}
                    </Typography>
                  </Paper>
                </InfoWindow>
              )}

              {/* Circle around selected location */}
              {selected.item?.location && (
                <>
                  <Circle
                    center={selected.item.location}
                    radius={circleRadius}
                    options={{
                      fillColor: "#FF0000",
                      fillOpacity: 0.08,
                      strokeColor: "#FF0000",
                      strokeOpacity: 0.6,
                      strokeWeight: 2,
                    }}
                  />
                  <Circle
                    center={selected.item.location}
                    radius={circleRadius + 100}
                    options={{
                      fillColor: "#FF0000",
                      fillOpacity: 0.03,
                      strokeColor: "#FF0000",
                      strokeOpacity: 0.3,
                      strokeWeight: 1,
                    }}
                  />
                </>
              )}

              {/* InfoWindow for selected item */}
              {selected.item?.location && !hoveredCone && showInfoWindow && (
                <InfoWindow
                  position={selected.item.location}
                  onCloseClick={() => {
                    setSelected({ theta: 0, item: {} });
                    setShowFireCones(false);
                    setFireCones([]);
                    setShowInfoWindow(true);
                  }}
                  options={{
                    pixelOffset: new window.google.maps.Size(0, -10),
                    maxWidth: 360,
                    disableAutoPan: false,
                    enableEventPropagation: true,
                  }}
                >
                  <Box sx={{ p: 0, m: 0 }}>
                    <Paper
                      sx={{
                        p: 1.5,
                        minWidth: 260,
                        maxWidth: 340,
                        bgcolor: "rgba(255, 255, 255, 0.98)",
                        backdropFilter: "blur(10px)",
                        borderRadius: 2,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                        position: "relative",
                        overflow: "visible",
                        border: "1px solid rgba(0,0,0,0.08)",
                      }}
                    >
                      {/* Close Button */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelected({ theta: 0, item: {} });
                          setShowFireCones(false);
                          setFireCones([]);
                          setShowInfoWindow(true);
                        }}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          zIndex: 2,
                          bgcolor: "rgba(0,0,0,0.05)",
                          "&:hover": {
                            bgcolor: "rgba(0,0,0,0.1)",
                          },
                          p: 0.5,
                          width: 24,
                          height: 24,
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16, color: "#666" }} />
                      </IconButton>

                      {/* Name and Status */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          mt: 0.5,
                          pr: 3,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <LocationIcon
                            sx={{
                              color: "#ff4444",
                              fontSize: 18,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="#333"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.85rem",
                            }}
                          >
                            {selected.item.user
                              ? selected.item.description
                              : selected.item.name ||
                                selected.item.description ||
                                "Unknown Location"}
                          </Typography>
                        </Box>

                        {/* Status Badge for Cameras */}
                        {isCamera && cameraStatus && (
                          <Chip
                            size="small"
                            icon={
                              <FiberManualRecordIcon
                                sx={{
                                  fontSize: 8,
                                  color: cameraStatus.iconColor,
                                  animation: cameraStatus.pulse
                                    ? "pulse 1.5s infinite"
                                    : "none",
                                  "@keyframes pulse": {
                                    "0%": { opacity: 1 },
                                    "50%": { opacity: 0.3 },
                                    "100%": { opacity: 1 },
                                  },
                                }}
                              />
                            }
                            label={cameraStatus.label}
                            color={cameraStatus.chipColor}
                            sx={{
                              height: 20,
                              fontSize: "0.6rem",
                              fontWeight: 600,
                              flexShrink: 0,
                              "& .MuiChip-label": { px: 0.8, py: 0 },
                            }}
                          />
                        )}
                      </Box>

                      {/* Lat/Long with Copy Button */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 1,
                          p: 0.75,
                          bgcolor: "rgba(0,0,0,0.04)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ flex: 1, fontSize: "0.7rem" }}
                        >
                          📌 {selected.item.location.lat.toFixed(6)},{" "}
                          {selected.item.location.lng.toFixed(6)}
                        </Typography>
                        <Tooltip title="Copy Coordinates">
                          <IconButton
                            size="small"
                            onClick={() =>
                              copyToClipboard(
                                `${selected.item.location.lat.toFixed(6)}, ${selected.item.location.lng.toFixed(6)}`,
                              )
                            }
                            sx={{ p: 0.3 }}
                          >
                            <ContentCopyIcon
                              sx={{ fontSize: 14, color: "#666" }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Action Buttons - Show both Camera and Sensor buttons if available */}
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        {/* Camera Section */}
                        {isCamera && (
                          <Stack
                            direction="column"
                            spacing={0.5}
                            sx={{ flex: 1 }}
                          >
                            {selected.item.id && (
                              <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="small"
                                startIcon={
                                  <EventNoteIcon sx={{ fontSize: 16 }} />
                                }
                                href={`/live/${selected.item.id}`}
                                target="_blank"
                                sx={{
                                  borderRadius: 1.5,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  py: 0.75,
                                }}
                              >
                                Event Details
                              </Button>
                            )}
                            {isArmed && (
                              <Button
                                variant="contained"
                                color="success"
                                fullWidth
                                size="small"
                                startIcon={
                                  <VideocamIcon sx={{ fontSize: 16 }} />
                                }
                                href="http://203.135.63.37:8008/"
                                target="_blank"
                                sx={{
                                  borderRadius: 1.5,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  py: 0.75,
                                }}
                              >
                                Live Stream
                              </Button>
                            )}
                          </Stack>
                        )}

                        {/* Sensor Section */}
                        {isSensor && selected.item.things_board_link && (
                          <Stack
                            direction="column"
                            spacing={0.5}
                            sx={{ flex: 1 }}
                          >
                            <Button
                              variant="contained"
                              color="info"
                              fullWidth
                              size="small"
                              startIcon={
                                <DashboardIcon sx={{ fontSize: 16 }} />
                              }
                              onClick={() => handleOpenDashboard(selected.item)}
                              sx={{
                                borderRadius: 1.5,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                py: 0.75,
                              }}
                            >
                              Weather Dashboard
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    </Paper>
                  </Box>
                </InfoWindow>
              )}

              {/* Render Fire Footprints */}
              {fireFootprints?.features?.map((feature, idx) => {
                if (feature.geometry.type === "Polygon") {
                  const isInsidePakistan = feature.geometry.coordinates[0].some(
                    ([lng, lat]) => isCoordinateInsidePakistan(lat, lng),
                  );
                  if (isInsidePakistan) {
                    return (
                      <Polygon
                        key={idx}
                        path={feature.geometry.coordinates[0].map(
                          ([lng, lat]) => ({ lat, lng }),
                        )}
                        options={{
                          fillColor: "#FF0000",
                          fillOpacity: 0.35,
                          strokeColor: "#FFFFFF",
                          strokeOpacity: 0.7,
                          strokeWeight: 1.5,
                        }}
                      />
                    );
                  }
                } else if (feature.geometry.type === "Point") {
                  if (
                    isCoordinateInsidePakistan(
                      feature.geometry.coordinates[1],
                      feature.geometry.coordinates[0],
                    )
                  ) {
                    return (
                      <Marker
                        key={idx}
                        position={{
                          lat: feature.geometry.coordinates[1],
                          lng: feature.geometry.coordinates[0],
                        }}
                        icon={{
                          url: getMarkerIcon(feature.properties.description),
                          scaledSize: new window.google.maps.Size(22, 22),
                        }}
                        onClick={() => setSelectedFeature(feature)}
                      />
                    );
                  }
                }
                return null;
              })}

              {selectedFeature && (
                <InfoWindow
                  position={{
                    lat: selectedFeature.geometry.coordinates[1],
                    lng: selectedFeature.geometry.coordinates[0],
                  }}
                  onCloseClick={() => setSelectedFeature(null)}
                >
                  <Paper sx={{ p: 0, maxWidth: 250 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {selectedFeature.properties.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      dangerouslySetInnerHTML={{
                        __html: selectedFeature.properties.description,
                      }}
                    />
                  </Paper>
                </InfoWindow>
              )}
            </GoogleMap>
          )}
        </Box>
      </Box>

      {/* Right Sidebar - Event List */}
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "rgba(20,20,30,0.95)",
          backdropFilter: "blur(10px)",
          borderLeft: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#fff" }}>
            📋 Events
          </Typography>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <EventList eventClick={(item) => onSelect(item)} />
        </Box>
      </Box>

      {/* Weather Dashboard Modal */}
      <Dialog
        open={dashboardOpen}
        onClose={handleCloseDashboard}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "98vh",
            bgcolor: "#1e1e1e",
            borderRadius: "20px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#2a2a2a",
            color: "white",
            borderBottom: "1px solid #333",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Logo - conditional based on user */}
            {isPunjabAdmin ? (
              <Box
                component="img"
                src={forest_fire_new_logo}
                alt="Forestry & Wildlife Punjab"
                sx={{
                  height: 48,
                  width: "auto",
                  maxWidth: 200,
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />
            ) : (
              <Avatar
                src={fireLogo}
                alt="LUMS Logo"
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  border: "2px solid rgba(255,255,255,0.2)",
                  objectFit: "cover",
                }}
              />
            )}
            <DashboardIcon sx={{ color: "#ffd93d" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Weather Dashboard -{" "}
              {selectedSensorForDashboard?.name ||
                selectedSensorForDashboard?.sensor_type ||
                "Sensor"}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseDashboard}
            sx={{
              color: "#888",
              "&:hover": {
                color: "#ff4444",
                backgroundColor: "rgba(255,68,68,0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#1e1e1e", height: "100vh" }}>
          <iframe
            src={
              "https://icarus.lums.edu.pk/dashboard/c3e6ece0-539c-11f0-af37-719941c4ace8?publicId=05acac90-5beb-11f0-af37-719941c4ace8&state=W3siaWQiOiJkZWZhdWx0IiwicGFyYW1zIjp7ImVudGl0eUlkIjp7ImlkIjoiNDk1Y2RhZTEtZWY3Yy0xMWVmLWFmMzctNzE5OTQxYzRhY2U4IiwiZW50aXR5VHlwZSI6IkRFVklDRSJ9LCJlbnRpdHlOYW1lIjoiMjUwMi1MTEctQVd9fV0%3D"
            }
            title="Weather Dashboard"
            width="100%"
            height="98%"
            style={{
              border: "none",
              backgroundColor: "#1e1e1e",
            }}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default MapContainer;
